<template>
  <div class="wrapper">
    <Sidebar :class="!isOpenSidebar ? 'close-sidebar': ''" />
    <div id="wrapper-content" :class="!isOpenSidebar ? 'ml-0' : 'content-wrapper-margin'">
      <PageHeader :isListPage="true" @toggleSidebar="toggleSidebar" />
      <div class="dashboard-wrapper">
        <div class="content-top-bar">
          <div class="row align-items-center">
            <div class="col-sm-3 col-md-6">
              <input 
                v-model="searchText" 
                type="text" 
                class="text-input search-site-text" 
                placeholder="Search Assets by Name"
              >
            </div>
            <div class="col-sm-9 col-md-6">
              <div class="content-top-right">
                <div class="form-check">
                  <div class="archive_url">
                    <input 
                      ref="fileInput" 
                      type="file" 
                      @change="handleFileSelect" 
                      :accept="acceptedFileTypes"
                      style="display: none;"
                    >
                    <input 
                      v-model="assetName" 
                      type="text" 
                      placeholder="Asset Name"
                      @keyup.enter="uploadFile"
                    >
                    <a @click="triggerFileSelect" href="#" class="btn btn-default btn-black">Upload</a>
                  </div>
                </div>
                <div class="form-check">
                  <div class="archiv_btn pointer">
                    <select v-model="filterType" @change="loadData" class="form-select">
                      <option value="">All Types</option>
                      <option value="script">Script Files</option>
                      <option value="style">Style Files</option>
                      <option value="image">Images</option>
                      <option value="video">Videos</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="dashboard-table" id="assets-table">
          <div v-if="loading" class="bg-gray-500 bg-opacity-25 fixed top-0 left-0 w-screen h-screen flex items-center justify-center">
            <div class="spinner-border" style="width: 3rem; height: 3rem;" role="status">
              <span class="visually-hidden">Loading...</span>
            </div>
          </div>
          <div v-else-if="filteredAssets.length === 0" class="h-screen flex items-center justify-center">
            <div class="text-lg">
              No assets found
            </div>
          </div>
          <div v-else>
            <table class="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Version</th>
                  <th>Type</th>
                  <th>Size</th>
                  <th>Uploaded</th>
                  <th class="text-end">Actions</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="asset of filteredAssets" :key="asset._id">
                  <td>
                    <div class="asset-name">
                      <i :class="getFileIcon(asset.file_type, asset.original_name)" class="me-2"></i>
                      {{ asset.name }}
                    </div>
                    <small class="text-muted">{{ asset.original_name }}</small>
                  </td>
                  <td>
                    <span class="badge bg-secondary">
                      v{{ asset.version || 1 }}
                    </span>
                    <small v-if="asset.version_meta?.version_count" class="text-muted ms-2">
                      {{ asset.version_meta.version_count }} total
                    </small>
                  </td>
                  <td>
                    <span class="badge" :class="getTypeBadgeClass(asset.file_type, asset.original_name)">
                      {{ getFileTypeLabel(asset.file_type, asset.original_name) }}
                    </span>
                  </td>
                  <td>{{ formatFileSize(asset.file_size) }}</td>
                  <td>{{ formatDate(asset.createdAt) }}</td>
                  <td>
                    <div class="dashboard-table-icons">
                      <CustomTooltip text="View" placement="top">
                        <button
                          class="pointer action-btn"
                          @click="viewAsset(asset)"
                        >
                          <i class="fa-solid fa-eye"></i>
                        </button>
                      </CustomTooltip>
                      <CustomTooltip v-if="isTextEditable(asset)" text="Edit" placement="top">
                        <button
                          class="pointer action-btn"
                          @click="openEdit(asset)"
                        >
                          <i class="fa-solid fa-pen-to-square"></i>
                        </button>
                      </CustomTooltip>
                      <CustomTooltip text="Download" placement="top">
                        <button
                          class="pointer action-btn"
                          @click="downloadAsset(asset)"
                        >
                          <i class="fa-solid fa-download"></i>
                        </button>
                      </CustomTooltip>
                      <CustomTooltip text="Versions" placement="top">
                        <button
                          class="pointer action-btn"
                          @click="openVersions(asset)"
                        >
                          <i class="fa-solid fa-code-branch"></i>
                        </button>
                      </CustomTooltip>
                      <CustomTooltip text="Delete" placement="top">
                        <button
                          class="pointer action-btn text-danger"
                          @click="deleteAsset(asset)"
                        >
                          <i class="fa-solid fa-trash"></i>
                        </button>
                      </CustomTooltip>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <!-- Edit Modal -->
        <div v-if="editModalOpen" class="fixed top-0 left-0 w-screen h-screen flex items-center justify-center bg-black bg-opacity-50" style="z-index: 1000;">
          <div class="bg-white rounded shadow-lg w-11/12 max-w-3xl">
            <div class="p-4 border-b flex justify-between items-center">
              <div>
                <h3 class="m-0">Edit: {{ editingAsset?.original_name || editingAsset?.name }}</h3>
                <small class="text-muted">Default version: v{{ editingVersionModel?.published_version || editingAsset?.version || 1 }}</small>
              </div>
              <button class="action-btn" @click="closeEdit"><i class="fa-solid fa-xmark"></i></button>
            </div>
            <div class="p-4">
            <div class="d-flex align-items-center gap-2 mb-3">
              <label class="form-label m-0">Version</label>
              <select
                class="form-select w-auto"
                v-model="selectedEditVersionId"
                @change="handleEditVersionChange"
                :disabled="editingVersionLoading"
              >
                <option v-for="version in editingVersionList" :key="version._id" :value="version._id">
                  v{{ version.version || 1 }} • {{ formatDate(version.createdAt) }}
                </option>
              </select>
              <span v-if="editingVersionLoading" class="text-muted">
                <i class="fa-solid fa-spinner fa-spin me-1"></i> Loading...
              </span>
            </div>
            <div class="mb-3">
              <label class="form-label">Version Note</label>
              <input
                type="text"
                class="form-control"
                placeholder="Describe this version (optional)"
                v-model="editVersionNote"
                :disabled="editingVersionLoading"
              />
            </div>
              <TemplateEditor
                :template="editContent"
                @set="code => editContent = code"
                style="min-height: 50vh;"
              />
            </div>
            <div class="p-3 border-t flex justify-end gap-2">
              <button class="btn btn-default" @click="closeEdit">Cancel</button>
              <button class="btn btn-primary" :disabled="savingEdit" @click="saveEdit">
                <span v-if="savingEdit" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Save
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <!-- Versions Modal -->
      <div v-if="versionsModalOpen" class="fixed top-0 left-0 w-screen h-screen flex items-center justify-center bg-black bg-opacity-50" style="z-index: 1000;">
        <div class="bg-white rounded shadow-lg w-11/12 max-w-4xl max-h-screen flex flex-col">
          <div class="p-4 border-b flex justify-between items-center">
            <div>
              <h3 class="m-0">Versions: <strong>{{ versionTarget?.name }}</strong></h3>
              <div class="text-muted text-sm">
                Default version: <strong>v{{ versionModelMeta?.published_version || versionTarget?.version || 1 }}</strong>
              </div>
              <small class="text-muted">Select an existing version, choose a default, or create a new one.</small>
            </div>
            <button class="action-btn" @click="closeVersions"><i class="fa-solid fa-xmark"></i></button>
          </div>
          <div class="flex-1 overflow-auto p-4">
            <div v-if="versionsLoading" class="d-flex justify-content-center align-items-center" style="min-height: 200px;">
              <div class="spinner-border" role="status">
                <span class="visually-hidden">Loading...</span>
              </div>
            </div>
            <div v-else-if="versionList.length === 0" class="text-center text-muted py-5">
              No versions found for this asset.
            </div>
            <div v-else class="table-responsive">
              <table class="table mb-0">
                <thead>
                  <tr>
                    <th>Version</th>
                    <th>Size</th>
                    <th>Uploaded</th>
                    <th>Notes</th>
                    <th class="text-end">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="version in versionList" :key="version._id">
                    <td>
                      <span class="badge bg-secondary">v{{ version.version || 1 }}</span>
                      <span v-if="versionModelMeta?.published_version === version.version" class="ms-2 text-xs text-primary">(Default)</span>
                      <span v-if="version._id === versionTarget?._id" class="ms-2 text-xs text-success">(Current)</span>
                    </td>
                    <td>{{ formatFileSize(version.file_size) }}</td>
                    <td>{{ formatDate(version.createdAt) }}</td>
                    <td class="text-sm text-gray-600">
                      {{ version.version_note || '—' }}
                    </td>
                    <td class="text-end">
                      <div class="dashboard-table-icons justify-end">
                        <CustomTooltip text="Set as default" placement="top">
                          <button
                            class="pointer action-btn"
                            :disabled="settingDefaultVersionId === version._id || versionModelMeta?.published_version === version.version"
                            @click="setDefaultVersion(version)"
                          >
                            <span v-if="settingDefaultVersionId === version._id" class="spinner-border spinner-border-sm"></span>
                            <i v-else :class="versionModelMeta?.published_version === version.version ? 'fa-solid fa-star text-primary' : 'fa-solid fa-star'"></i>
                          </button>
                        </CustomTooltip>
                        <CustomTooltip text="View" placement="top">
                          <button class="pointer action-btn" @click="viewAsset(version)">
                            <i class="fa-solid fa-eye"></i>
                          </button>
                        </CustomTooltip>
                        <CustomTooltip text="Edit note" placement="top">
                          <button class="pointer action-btn" @click="openVersionNoteModal(version)">
                            <i class="fa-solid fa-pen-to-square"></i>
                          </button>
                        </CustomTooltip>
                        <CustomTooltip text="Download" placement="top">
                          <button class="pointer action-btn" @click="downloadAsset(version)">
                            <i class="fa-solid fa-download"></i>
                          </button>
                        </CustomTooltip>
                      </div>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="p-4 border-t flex flex-col gap-3 justify-between">
            <div class="flex flex-col gap-2 flex-1 md:flex-row md:items-center">
              <div class="d-flex gap-2">
                <button class="btn btn-default" @click="closeVersions">Close</button>
                <button class="btn btn-primary" :disabled="creatingNewVersion" @click="createNewVersion">
                  <span v-if="creatingNewVersion" class="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                  Create New Version
                </button>
              </div>
              <div class="flex-1 justify-end w-full">
                <input
                  v-model="newVersionNote"
                  type="text"
                  class="form-control form-control-sm"
                  placeholder="Note for new version (optional)"
                />
              </div>
            </div>
            <div class="text-sm text-muted">
              Creating a new version duplicates the current default file. You can edit its content afterwards.
            </div>
          </div>
        </div>
      </div>
    </div>
    <vue-final-modal v-model="showVersionNoteModal" :click-to-close="false">
      <div class="bg-white w-full max-w-md mx-auto my-32 rounded shadow">
        <div class="px-4 py-3 border-b flex items-center justify-between">
          <div>
            <div class="font-semibold text-lg">Edit Version Note</div>
            <div class="text-sm text-gray-500">
              v{{ noteEditVersion?.version || 1 }} • {{ noteEditVersion?.original_name }}
            </div>
          </div>
          <button class="action-btn" @click="closeVersionNoteModal">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <div class="px-4 py-4">
          <label class="form-label">Version Note</label>
          <textarea
            class="form-control"
            rows="3"
            v-model="noteEditValue"
            placeholder="Describe this version"
          ></textarea>
        </div>
        <div class="px-4 py-3 border-t flex justify-end gap-2">
          <button class="btn btn-default" @click="closeVersionNoteModal">Cancel</button>
          <button class="btn btn-primary" :disabled="noteSaving" @click="saveVersionNote">
            <span v-if="noteSaving" class="spinner-border spinner-border-sm me-2"></span>
            Save
          </button>
        </div>
      </div>
    </vue-final-modal>
  </div>
</template>

<script>
import PageHeader from './components/PageHeader'
import Sidebar from '@/components/Sidebar.vue'
import * as assetService from '../service/asset'
import CustomTooltip from "@/utilities/CustomTooltip.vue"
import TemplateEditor from "../components/TemplateEditor.vue"

export default {
  name: 'AssetList',
  components: {
    PageHeader,
    Sidebar,
    CustomTooltip,
    TemplateEditor,
  },
  data() {
    return {
      loading: false,
      assets: [],
      searchText: '',
      assetName: '',
      selectedFile: null,
      filterType: '',
      isOpenSidebar: true,
      editModalOpen: false,
      editingAsset: null,
      editContent: '',
      savingEdit: false,
      versionsModalOpen: false,
      versionsLoading: false,
      versionList: [],
      versionTarget: null,
      acceptedFileTypes: '.js,.jsx,.ts,.tsx,.vue,.css,.scss,.sass,.less,.html,.htm,.py,.java,.c,.cpp,.h,.hpp,.php,.rb,.go,.rs,.json,.xml,.md,.txt,.log,.env,.gitignore,.dockerfile,.jpg,.jpeg,.png,.gif,.svg,.webp,.bmp,.tiff,.tif,.mp4,.avi,.mov,.wmv,.flv,.webm,.mkv,.3gp',
      versionModelMeta: null,
      settingDefaultVersionId: null,
      editingVersionList: [],
      editingVersionModel: null,
      selectedEditVersionId: null,
      editingVersionLoading: false,
      creatingNewVersion: false,
      newVersionNote: '',
      editVersionNote: '',
      showVersionNoteModal: false,
      noteEditVersion: null,
      noteEditValue: '',
      noteSaving: false,
    }
  },
  computed: {
    filteredAssets() {
      let filtered = this.assets
      
      // Filter by search text
      if (this.searchText.trim()) {
        const search = this.searchText.trim().toLowerCase()
        filtered = filtered.filter(asset => 
          asset.name.toLowerCase().includes(search) || 
          asset.original_name.toLowerCase().includes(search)
        )
      }
      
      // Filter by type
      if (this.filterType) {
        filtered = filtered.filter(asset => {
          const fileType = asset.file_type
          if (this.filterType === 'script') {
            return fileType === 'application/javascript' || 
                   fileType === 'text/javascript' || 
                   fileType === 'application/x-javascript' ||
                   (fileType.startsWith('text/') && asset.original_name.match(/\.(js|jsx|ts|tsx)$/i))
          } else if (this.filterType === 'style') {
            return fileType === 'text/css' || 
                   (fileType.startsWith('text/') && asset.original_name.match(/\.(css|scss|sass|less)$/i))
          } else if (this.filterType === 'image') {
            return fileType.startsWith('image/')
          } else if (this.filterType === 'video') {
            return fileType.startsWith('video/')
          }
          return true
        })
      }
      
      return filtered
    }
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const response = await assetService.getAssets({ take: 200, view: 'latest' })
        this.assets = response.assets || []
      } catch (err) {
        console.log('Error loading assets:', err)
        this.$notify({
          type: "error",
          text: "Failed to load assets"
        })
      }
      this.loading = false
    },
    
    triggerFileSelect() {
      this.$refs.fileInput.click()
    },

    async openVersions(asset) {
      this.versionTarget = asset
      this.versionModelMeta = null
      this.versionsModalOpen = true
      await this.loadVersions(asset._id)
    },

    async loadVersions(assetId) {
      this.versionsLoading = true
      try {
        const res = await assetService.getAssetVersions(assetId)
        this.versionModelMeta = res.versionModel || null
        this.versionList = (res.versions || []).map(version => ({
          ...version,
          version: version.version || 1
        }))
        if (this.versionList.length) {
          this.versionTarget = this.versionList.find(v => v.version === this.versionModelMeta?.current_version) || this.versionList[0]
        }
      } catch (err) {
        console.log('Error loading versions:', err)
        this.$notify({
          type: "error",
          text: "Failed to load versions"
        })
      }
      this.versionsLoading = false
    },

    async setDefaultVersion(version) {
      if (!this.versionModelMeta?._id) {
        return
      }
      this.settingDefaultVersionId = version._id
      try {
        await assetService.setAssetDefaultVersion(this.versionModelMeta._id, version.version || 1)
        this.versionModelMeta.published_version = version.version || 1
        this.$notify({
          type: "success",
          text: `Default version set to v${version.version || 1}`
        })
      } catch (err) {
        console.log('Error setting default version:', err)
        this.$notify({
          type: "error",
          text: err.message || "Failed to set default version"
        })
      }
      this.settingDefaultVersionId = null
    },

    closeVersions() {
      this.versionsModalOpen = false
      this.versionList = []
      this.versionTarget = null
      this.versionModelMeta = null
      this.creatingNewVersion = false
      this.newVersionNote = ''
    },

    async createNewVersion() {
      if (!this.versionTarget?._id) {
        return
      }
      this.creatingNewVersion = true
      try {
        await assetService.uploadAssetVersion(this.versionTarget._id, null, {
          versionNote: this.newVersionNote || ''
        })
        this.$notify({
          type: "success",
          text: "New version created from default file"
        })
        this.newVersionNote = ''
        await this.loadVersions(this.versionTarget._id)
        await this.loadData()
      } catch (err) {
        console.log('Version creation error:', err)
        this.$notify({
          type: "error",
          text: err.message || "Failed to create new version"
        })
      }
      this.creatingNewVersion = false
    },
    
    handleFileSelect(event) {
      const file = event.target.files[0]
      if (file) {
        this.selectedFile = file
        if (!this.assetName) {
          this.assetName = file.name.split('.')[0]
        }
        this.uploadFile()
      }
    },
    
    async uploadFile() {
      if (!this.selectedFile) {
        this.$notify({
          type: "warn",
          text: "Please select a file first"
        })
        return
      }
      
      if (!this.assetName.trim()) {
        this.$notify({
          type: "warn",
          text: "Please enter an asset name"
        })
        return
      }
      
      this.loading = true
      try {
        await assetService.uploadAsset(this.selectedFile, this.assetName.trim())
        this.$notify({
          type: "success",
          text: "Asset uploaded successfully"
        })
        this.selectedFile = null
        this.assetName = ''
        this.$refs.fileInput.value = ''
        this.loadData()
      } catch (err) {
        console.log('Upload error:', err)
        this.$notify({
          type: "error",
          text: err.message || "Failed to upload asset"
        })
      }
      this.loading = false
    },
    
    viewAsset(asset) {
      const url = assetService.getAssetUrl(asset.s3_key)
      window.open(url, '_blank')
    },
    
    downloadAsset(asset) {
      const url = assetService.getAssetUrl(asset.s3_key)
      const link = document.createElement('a')
      link.href = url
      link.download = asset.original_name
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    
    async deleteAsset(asset) {
      const confirm = window.confirm(`Are you sure you want to delete "${asset.name}"?`)
      if (!confirm) return
      
      try {
        await assetService.deleteAsset(asset._id)
        this.$notify({
          type: "success",
          text: "Asset deleted successfully"
        })
        this.loadData()
      } catch (err) {
        console.log('Delete error:', err)
        this.$notify({
          type: "error",
          text: "Failed to delete asset"
        })
      }
    },
    
    isTextEditable(asset) {
      const type = asset.file_type || ''
      const name = (asset.original_name || '').toLowerCase()
      if (type.startsWith('text/') || type === 'application/json') return true
      return !!name.match(/\.(js|jsx|ts|tsx|vue|css|scss|sass|less|html|htm|py|java|c|cpp|h|hpp|php|rb|go|rs|json|xml|md|txt|log|env|gitignore|dockerfile)$/i)
    },

    async openEdit(asset) {
      try {
        this.loading = true
        await this.loadEditVersions(asset._id)
        if (this.editingAsset) {
          this.editModalOpen = true
        }
      } catch (err) {
        console.log('Open edit error:', err)
        this.$notify({ type: 'error', text: 'Failed to load file content' })
      } finally {
        this.loading = false
      }
    },

    closeEdit() {
      this.editModalOpen = false
      this.editingAsset = null
      this.editingVersionList = []
      this.editingVersionModel = null
      this.selectedEditVersionId = null
      this.editContent = ''
      this.editVersionNote = ''
    },

    async saveEdit() {
      if (!this.editingAsset) return
      this.savingEdit = true
      try {
        const contentType = this.editingAsset.file_type || 'text/plain'
        await assetService.uploadTextToKey(this.editingAsset.s3_key, this.editContent, contentType)
        await assetService.updateAssetVersionMeta(this.editingAsset._id, {
          versionNote: this.editVersionNote || ''
        })
        this.$notify({ type: 'success', text: 'File saved' })
        this.closeEdit()
      } catch (err) {
        console.log('Save edit error:', err)
        this.$notify({ type: 'error', text: 'Failed to save file' })
      } finally {
        this.savingEdit = false
      }
    },

    async loadEditVersions(assetId) {
      this.editingVersionLoading = true
      try {
        const res = await assetService.getAssetVersions(assetId)
        this.editingVersionModel = res.versionModel || null
        this.editingVersionList = (res.versions || []).map(v => ({
          ...v,
          version: v.version || 1
        }))
        const defaultVersion = this.editingVersionModel?.current_version
        const selected = this.editingVersionList.find(v => v.version === defaultVersion) || this.editingVersionList[0]
        this.selectedEditVersionId = selected?._id || null
        if (selected) {
          await this.loadEditVersionContent(selected)
        } else {
          this.editContent = ''
        }
      } catch (err) {
        console.log('Failed to load edit versions:', err)
        this.$notify({ type: 'error', text: 'Failed to load asset versions' })
      }
      this.editingVersionLoading = false
    },

    async loadEditVersionContent(version) {
      try {
        this.editingAsset = version
        const url = assetService.getAssetUrl(version.s3_key)
        const res = await fetch(url)
        const text = await res.text()
        this.editContent = text
        this.editVersionNote = version.version_note || ''
      } catch (err) {
        console.log('Failed to load version content:', err)
        this.$notify({ type: 'error', text: 'Failed to load version content' })
      }
    },

    async handleEditVersionChange() {
      const version = this.editingVersionList.find(v => v._id === this.selectedEditVersionId)
      if (!version) {
        return
      }
      await this.loadEditVersionContent(version)
    },
    openVersionNoteModal(version) {
      this.noteEditVersion = version
      this.noteEditValue = version.version_note || ''
      this.showVersionNoteModal = true
    },
    closeVersionNoteModal() {
      this.showVersionNoteModal = false
      this.noteEditVersion = null
      this.noteEditValue = ''
      this.noteSaving = false
    },
    async saveVersionNote() {
      if (!this.noteEditVersion) {
        return
      }
      this.noteSaving = true
      try {
        await assetService.updateAssetVersionMeta(this.noteEditVersion._id, {
          versionNote: this.noteEditValue || ''
        })
        const target = this.versionList.find(v => v._id === this.noteEditVersion._id)
        if (target) {
          target.version_note = this.noteEditValue
        }
        if (this.versionTarget && this.versionTarget._id === this.noteEditVersion._id) {
          this.versionTarget.version_note = this.noteEditValue
        }
        if (this.editingAsset && this.editingAsset._id === this.noteEditVersion._id) {
          this.editVersionNote = this.noteEditValue
        }
        this.$notify({ type: 'success', text: 'Version note updated' })
        this.closeVersionNoteModal()
      } catch (err) {
        console.log('Failed to update version note:', err)
        this.$notify({ type: 'error', text: 'Failed to update version note' })
        this.noteSaving = false
      }
    },

    getFileIcon(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'fa-solid fa-image'
      if (fileType.startsWith('video/')) return 'fa-solid fa-video'
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'fa-solid fa-file-code'
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'fa-solid fa-palette'
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'fa-solid fa-file-code'
      return 'fa-solid fa-file'
    },
    
    getFileTypeLabel(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'Image'
      if (fileType.startsWith('video/')) return 'Video'
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'Script'
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'Style'
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'Code'
      return 'File'
    },
    
    getTypeBadgeClass(fileType, originalName = '') {
      if (fileType.startsWith('image/')) return 'bg-success'
      if (fileType.startsWith('video/')) return 'bg-primary'
      if (fileType === 'application/javascript' || 
          fileType === 'text/javascript' || 
          fileType === 'application/x-javascript' ||
          (fileType.startsWith('text/') && originalName.match(/\.(js|jsx|ts|tsx)$/i))) {
        return 'bg-warning'
      }
      if (fileType === 'text/css' || 
          (fileType.startsWith('text/') && originalName.match(/\.(css|scss|sass|less)$/i))) {
        return 'bg-info'
      }
      if (fileType.startsWith('text/') || fileType === 'application/json') return 'bg-info'
      return 'bg-secondary'
    },
    
    formatFileSize(bytes) {
      if (!bytes) return '0 B'
      const k = 1024
      const sizes = ['B', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    
    formatDate(date) {
      if (!date) return ''
      return new Date(date).toLocaleDateString()
    },
    
    toggleSidebar() {
      this.isOpenSidebar = !this.isOpenSidebar
    }
  }
}
</script>

<style scoped>
.close-sidebar {
  margin-left: -260px !important;
}

.search-site-text {
  width: 100%;
  max-width: 300px;
}

#assets-table {
  height: calc(100vh - 156px);
  overflow: auto;
}

.asset-name {
  font-weight: 500;
}

.dashboard-table-icons {
  display: flex;
  gap: 8px;
}

.action-btn {
  background: none;
  border: none;
  padding: 4px 8px;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.action-btn:hover {
  background-color: #f8f9fa;
}

.action-btn.text-danger:hover {
  background-color: #f8d7da;
}

.badge {
  font-size: 0.75em;
  padding-top: 6px;
}

.form-select {
  max-width: 150px;
}

.archiv_btn {
  margin-left: 10px;
  padding: 0;
}
</style>
